import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { getInterview, getRelated } from "../lib/api";
import InterviewCard from "../components/InterviewCard";

const Eyebrow = ({ children, tone = "blue" }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className={`h-px w-8 ${tone === "green" ? "bg-medgreen" : "bg-medblue"}`} />
    <span className={`text-[10px] uppercase tracking-[0.28em] ${tone === "green" ? "text-medgreen" : "text-medblue"}`}>{children}</span>
  </div>
);

const Section = ({ eyebrow, title, children, id }) => (
  <section id={id} className="py-16 border-t border-rule" data-testid={`section-${id}`}>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
      <div className="lg:col-span-4">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="font-serif text-3xl lg:text-[36px] text-navy leading-[1.15] tracking-tight">{title}</h2>
      </div>
      <div className="lg:col-span-8 text-[17px] text-navy/70 leading-[1.75] whitespace-pre-line">
        {children}
      </div>
    </div>
  </section>
);

// Parses essay text: lines starting with "## " become subsection headings, blank lines split paragraphs.
function renderEssay(text) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let currentTitle = null;
  let paragraphs = [];
  let buf = [];

  const flushPara = () => {
    if (buf.length) { paragraphs.push(buf.join(" ").trim()); buf = []; }
  };
  const flushBlock = () => {
    flushPara();
    if (currentTitle || paragraphs.length) {
      blocks.push({ title: currentTitle, paragraphs });
      paragraphs = [];
      currentTitle = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("## ")) {
      flushBlock();
      currentTitle = line.slice(3).trim();
    } else if (line === "") {
      flushPara();
    } else {
      buf.push(line);
    }
  }
  flushBlock();
  return blocks;
}

function EssaySection({ essay }) {
  const blocks = renderEssay(essay);
  if (!blocks.length) return null;
  const [intro, ...takeaways] = blocks;
  const hasIntroOnly = intro && !intro.title;

  return (
    <>
      {hasIntroOnly && (
        <section className="py-16 border-t border-rule" data-testid="section-about">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>About</Eyebrow>
              <h2 className="font-serif text-3xl lg:text-[36px] text-navy leading-[1.15] tracking-tight">
                The conversation.
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-5 text-[17px] text-navy/75 leading-[1.8]">
              {intro.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </section>
      )}
      {takeaways.length > 0 && (
        <section className="py-16 border-t border-rule" data-testid="section-takeaways">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-4">
              <Eyebrow tone="green">Key Takeaways</Eyebrow>
              <h2 className="font-serif text-3xl lg:text-[36px] text-navy leading-[1.15] tracking-tight">
                What stayed with me.
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-14">
              {takeaways.map((t, i) => (
                <div key={i}>
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="font-mono text-xs text-medblue">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-serif text-2xl md:text-[28px] text-navy leading-[1.2]">{t.title}</h3>
                  </div>
                  <div className="pl-10 space-y-4 text-[17px] text-navy/75 leading-[1.8]">
                    {t.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function TranscriptSection({ transcript }) {
  const lines = transcript.replace(/\r\n/g, "\n").split("\n").map((l) => l.trim()).filter(Boolean);
  const items = [];
  const speakerRegex = /^([A-Za-zÀ-ÿ.'\-\s]{2,60}?):\s*(.*)$/;

  for (const line of lines) {
    const m = line.match(speakerRegex);
    if (m) {
      items.push({ speaker: m[1].trim(), text: m[2].trim() });
    } else if (items.length) {
      items[items.length - 1].text += " " + line;
    } else {
      items.push({ speaker: "", text: line });
    }
  }

  return (
    <section id="transcript" className="py-16 border-t border-rule" data-testid="section-transcript">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-4">
          <Eyebrow>Full Transcript</Eyebrow>
          <h2 className="font-serif text-3xl lg:text-[36px] text-navy leading-[1.15] tracking-tight">The conversation, in full.</h2>
        </div>
        <div className="lg:col-span-8 space-y-8">
          {items.map((it, i) => {
            const isHost = /saveer/i.test(it.speaker);
            return (
              <div key={i} className={isHost ? "" : "border-l-2 border-medblue pl-6"}>
                {it.speaker && (
                  <div className={`text-[10px] uppercase tracking-[0.24em] mb-2 ${isHost ? "text-navy/50" : "text-medblue"}`}>
                    {it.speaker}
                  </div>
                )}
                <p className={`text-[17px] leading-[1.85] ${isHost ? "text-navy/60 italic" : "text-navy/85"}`}>{it.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Convert a YouTube watch/short URL to embed URL. Returns original if unknown.
function toEmbedUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
      if (u.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}`;
      if (u.pathname.startsWith("/embed/")) return url;
    }
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video${u.pathname}`;
  } catch { /* fall through */ }
  return url;
}

export default function InterviewDetail() {
  const { id } = useParams();
  const [iv, setIv] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    getInterview(id).then((data) => { setIv(data); setLoading(false); }).catch(() => { setError(true); setLoading(false); });
    getRelated(id).then(setRelated).catch(() => {});
  }, [id]);

  if (loading) return <div className="container-wide py-32 text-center text-navy/50">Loading…</div>;
  if (error || !iv) {
    return (
      <div className="container-wide py-32 text-center" data-testid="not-found">
        <div className="font-serif text-4xl text-navy/35 italic">Interview not found.</div>
        <Link to="/interviews" className="mt-8 btn-secondary inline-flex">Back to archive</Link>
      </div>
    );
  }

  const embedUrl = toEmbedUrl(iv.video_url);

  return (
    <div data-testid="interview-detail" className="bg-white">
      <section className="border-b border-rule">
        <div className="container-wide py-14">
          <Link to="/interviews" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-navy/60 hover:text-navy" data-testid="back-link">
            <ArrowLeft size={13} /> Back to archive
          </Link>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <Eyebrow>{iv.category}</Eyebrow>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-[68px] text-navy leading-[1.03] tracking-tight" data-testid="interview-name">
                {iv.name}
              </h1>
              <div className="mt-6 text-lg text-navy/65">
                {iv.profession}{iv.specialty ? ` · ${iv.specialty}` : ""}
              </div>
              {iv.organization && (
                <div className="mt-1 text-sm text-navy/50">{iv.organization}</div>
              )}
              {iv.biography && (
                <p className="mt-8 text-lg text-navy/70 leading-[1.75] max-w-2xl">{iv.biography}</p>
              )}
            </div>
            <div className="lg:col-span-5">
              {iv.profile_image && (
                <img src={iv.profile_image} alt={iv.name} className="w-full aspect-[4/5] object-cover grayscale contrast-105" />
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="container-wide">
        {iv.essay && <EssaySection essay={iv.essay} />}
        {iv.chapters && iv.chapters.length > 0 ? (
          <section className="py-20 border-t border-rule" data-testid="section-chapters">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-4">
                <Eyebrow tone="green">In This Conversation</Eyebrow>
                <h2 className="font-serif text-3xl lg:text-[36px] text-navy leading-[1.15] tracking-tight">
                  What we talked about.
                </h2>
              </div>
              <div className="lg:col-span-8">
                <ol className="divide-y divide-rule border-y border-rule">
                  {iv.chapters.map((c, i) => (
                    <li key={i} className="flex items-baseline gap-6 py-5 group">
                      <span className="font-mono text-xs text-medblue shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-serif text-xl md:text-2xl text-navy leading-snug">{c}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>
        ) : iv.excerpt ? (
          <section className="py-20 border-t border-rule" data-testid="section-quote">
            <div className="max-w-4xl mx-auto text-center">
              <Quote size={28} className="text-medblue mx-auto" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.28em] text-medblue mt-4">Favorite Quote</div>
              <blockquote className="mt-6 font-serif text-3xl md:text-4xl lg:text-[42px] text-navy leading-[1.2] tracking-tight italic">
                &ldquo;{iv.excerpt}&rdquo;
              </blockquote>
              <div className="mt-8 text-[11px] uppercase tracking-[0.24em] text-navy/50">— {iv.name}</div>
            </div>
          </section>
        ) : null}
        {embedUrl && (
          <section className="py-16 border-t border-rule" data-testid="section-video">
            <Eyebrow>Watch The Interview</Eyebrow>
            <h2 className="font-serif text-3xl lg:text-[36px] text-navy leading-[1.15] tracking-tight mb-8 max-w-2xl">
              Watch the full conversation with {iv.name}.
            </h2>
            <div className="aspect-video border border-rule bg-parchment">
              <iframe src={embedUrl} title={`${iv.name} interview`} className="w-full h-full" allowFullScreen frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
            </div>
          </section>
        )}
        {iv.career_journey && <Section id="journey" eyebrow="Career Journey" title="The path taken.">{iv.career_journey}</Section>}
        {iv.advice && <Section id="advice" eyebrow="Student Advice" title="For students entering medicine.">{iv.advice}</Section>}
        {iv.insights && <Section id="insights" eyebrow="Healthcare Insights" title="Where medicine is going.">{iv.insights}</Section>}
        {iv.research && <Section id="research" eyebrow="Research & Innovation" title="Work and contributions.">{iv.research}</Section>}
        {iv.transcript && <TranscriptSection transcript={iv.transcript} />}
        {iv.audio_url && (
          <section className="py-16 border-t border-rule" data-testid="section-audio">
            <Eyebrow>Listen</Eyebrow>
            <audio controls src={iv.audio_url} className="w-full" />
          </section>
        )}
      </div>

      {related.length > 0 && (
        <section className="bg-parchment py-24 mt-14" data-testid="related-section">
          <div className="container-wide">
            <Eyebrow>Continue reading</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight mb-12">Related conversations.</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r, i) => <InterviewCard key={r.id} interview={r} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-rule">
        <div className="container-wide py-16 text-center">
          <Link to="/interviews" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-medblue hover:text-navy">
            View all interviews <ArrowRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
